import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Card, CardContent } from '../../../components/ui/Card.js';
import { Button } from '../../../components/ui/Button.js';
import { FormSelect } from '../../../components/ui/FormSelect.js';
import { StatusBadge } from '../../../components/ui/StatusBadge.js';
import { Money } from '../../../components/ui/Money.js';
import { Printer, FileDown, Landmark } from 'lucide-react';
import { PageHeader } from '../../../components/layout/PageHeader';
import { useQuery } from '@tanstack/react-query';
import { fetcher } from '../../../lib/api-fetcher';
import { TableSkeleton } from '../../../components/ui/Skeleton';
import { EmptyState } from '../../../components/ui/EmptyState';
import { useToast } from '../../../components/ui/Toast';
import { useSchemes, useSchemeSubscribers } from '../../../features/schemes/network/index.js';
import { useRunReport, useReportRun, useDownloadRun } from '../../../features/reporting/hooks';

export const Route = createFileRoute('/_auth/reports/subscriber-ledger')({
  component: SubscriberLedgerPage,
});

// Matches AccountLedgerRow (packages/shared/ledger) — the real GL-account ledger, not a
// denormalized report view. Sourced from ChitLedgerQueryService.getSubscriberLedger via
// GET /staff/v1/chit-groups/:schemeId/ledger/subscribers/:subId, which was already built and
// working but had zero frontend consumers until now.
interface LedgerRow {
  lineId: string;
  entryId: string;
  entryNumber: string;
  entryDate: string;
  description: string;
  narration: string | null;
  dr: number;
  cr: number;
  runningBalance: number;
}

interface AccountLedgerDetails {
  accountId: string;
  accountCode: string;
  accountName: string;
  accountType: string;
  initialBalance: number;
  closingBalance: number;
  rows: LedgerRow[];
}

function SubscriberLedgerPage() {
  const [selectedScheme, setSelectedScheme] = React.useState('');
  const [selectedSubscriber, setSelectedSubscriber] = React.useState('');
  const [activeRunId, setActiveRunId] = React.useState<string | null>(null);

  const { data: schemesResponse } = useSchemes({ limit: 100 });
  const schemes = schemesResponse?.data ?? [];
  const schemeOptions = schemes.map(s => ({ value: s.id, label: `${s.name} (${s.schemeNumber})` }));

  const { data: subscribersResponse } = useSchemeSubscribers(selectedScheme, { page: 1, limit: 100 });
  const subscribers = subscribersResponse?.data ?? [];
  const subscriberOptions = subscribers.map(s => ({
    value: s.id,
    label: `${s.customerName ?? 'Unnamed'} (Ticket ${s.ticketNumbers.join(', ')})`,
  }));

  React.useEffect(() => {
    if (schemeOptions.length > 0 && !selectedScheme) {
      setSelectedScheme(schemeOptions[0].value);
    }
  }, [schemeOptions.length]);

  React.useEffect(() => {
    setSelectedSubscriber('');
  }, [selectedScheme]);

  React.useEffect(() => {
    if (subscriberOptions.length > 0 && !selectedSubscriber) {
      setSelectedSubscriber(subscriberOptions[0].value);
    }
  }, [subscriberOptions.length]);

  const { data: ledger, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['subscriber-ledger', selectedScheme, selectedSubscriber],
    queryFn: () => fetcher.get<AccountLedgerDetails>(`/staff/v1/chit-groups/${selectedScheme}/ledger/subscribers/${selectedSubscriber}`),
    enabled: !!selectedScheme && !!selectedSubscriber,
  });

  const { toast } = useToast();
  const runMutation = useRunReport();
  const downloadMutation = useDownloadRun();
  const { data: runData } = useReportRun(activeRunId ?? '', { enabled: !!activeRunId });

  React.useEffect(() => {
    if (runData?.status === 'COMPLETED' && activeRunId) {
      downloadMutation.mutate(activeRunId, {
        onSuccess: () => {
          setActiveRunId(null);
        },
        onError: () => {
          setActiveRunId(null);
        }
      });
    } else if (runData?.status === 'FAILED') {
      setActiveRunId(null);
      toast({ type: 'error', title: 'Export Failed', description: 'Failed to generate report PDF.' });
    }
  }, [runData, activeRunId, toast]);

  const handleExportPdf = () => {
    if (!selectedSubscriber || !selectedScheme) return;
    setActiveRunId(null);
    runMutation.mutate({
      reportCode: 'SUBSCRIBER_LEDGER',
      parameters: { subscriber_id: selectedSubscriber, scheme_id: selectedScheme },
      format: 'PDF',
    }, {
      onSuccess: (data) => {
        if (data?.id) {
          setActiveRunId(data.id);
        }
      },
      onError: (err: unknown) => {
        const message = err instanceof Error ? err.message : 'Failed to start report generation';
        toast({ type: 'error', title: 'Export Error', description: message });
      }
    });
  };

  const rows = ledger?.rows ?? [];
  const totalPaid = rows.reduce((acc, r) => acc + r.cr, 0);
  const currentOutstanding = ledger?.closingBalance ?? 0;

  const action = (
    <div className="flex gap-2">
      <Button variant="secondary" onClick={() => window.print()}>
        <Printer className="w-4 h-4 mr-2" />
        Print Statement
      </Button>
      <Button
        variant="primary"
        onClick={handleExportPdf}
        loading={runMutation.isPending || !!activeRunId}
      >
        <FileDown className="w-4 h-4 mr-2" />
        {activeRunId ? 'Generating...' : 'Export PDF'}
      </Button>
    </div>
  );

  const toolbar = (
    <div className="bg-surface-2 p-4 rounded-xl border border-border grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-end max-w-5xl mb-4">
      <FormSelect
        label="Select Chit Scheme"
        value={selectedScheme}
        onChange={e => setSelectedScheme(e.target.value)}
        options={schemeOptions}
      />

      <FormSelect
        label="Select Subscriber"
        value={selectedSubscriber}
        onChange={e => setSelectedSubscriber(e.target.value)}
        options={subscriberOptions}
        disabled={!selectedScheme}
      />

      <div className="flex gap-3 justify-end pb-1 lg:col-span-1">
        <span className="text-xs font-semibold text-text-muted">Status:</span>
        <StatusBadge status="success" label="Active Subscription" />
      </div>
    </div>
  );

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="Subscriber Personal Ledger"
        subtitle="Auditable running statement of a specific subscriber's chit receivable account, sourced directly from the general ledger."
        action={action}
      />
      {toolbar}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl">
        <Card className="shadow-sm">
          <CardContent className="p-6">
            <span className="text-xs text-text-muted font-bold uppercase tracking-wider block">Total Credited (Paid + Dividends)</span>
            <h2 className="text-2xl font-bold text-success-text mt-1">
              <Money paise={totalPaid} />
            </h2>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardContent className="p-6">
            <span className="text-xs text-text-muted font-bold uppercase tracking-wider block">Current Outstanding Balance</span>
            <h2 className={`text-2xl font-bold mt-1 ${currentOutstanding > 0 ? 'text-danger-600' : 'text-text-primary'}`}>
              <Money paise={currentOutstanding} />
            </h2>
          </CardContent>
        </Card>
      </div>

      <div className="bg-surface-1 border border-border rounded-xl overflow-hidden shadow-sm max-w-5xl">
        {isLoading ? (
          <TableSkeleton columns={6} rows={5} />
        ) : isError ? (
          <div className="p-8 text-center">
            <p className="text-sm font-semibold text-danger-600">Failed to load ledger</p>
            <p className="text-xs text-text-muted mt-1">{error?.message || 'Unknown error'}</p>
            <Button variant="secondary" size="sm" className="mt-3" onClick={() => refetch()}>Retry</Button>
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            title="No transactions found"
            description="No posted ledger entries found for the selected subscriber."
            icon={<Landmark className="w-8 h-8" />}
          />
        ) : (
          <table className="w-full border-collapse text-left text-sm">
            <thead className="bg-surface-2 text-text-secondary font-semibold uppercase tracking-wider text-xs border-b border-border">
              <tr>
                <th className="p-4">Date</th>
                <th className="p-4">Voucher No.</th>
                <th className="p-4">Description</th>
                <th className="p-4">Due (Dr)</th>
                <th className="p-4">Credited (Cr)</th>
                <th className="p-4 text-right">Running Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border text-text-primary">
              {rows.map(r => (
                <tr key={r.lineId} className="hover:bg-surface-2 transition-colors">
                  <td className="p-4 font-mono text-xs">{r.entryDate}</td>
                  <td className="p-4 font-mono text-xs text-text-muted">{r.entryNumber}</td>
                  <td className="p-4 font-semibold">{r.narration || r.description}</td>
                  <td className="p-4">
                    {r.dr > 0 ? <Money paise={r.dr} /> : '—'}
                  </td>
                  <td className="p-4 text-success-text font-semibold">
                    {r.cr > 0 ? <Money paise={r.cr} /> : '—'}
                  </td>
                  <td className={`p-4 text-right font-mono font-bold ${r.runningBalance > 0 ? 'text-danger-600' : 'text-text-secondary'}`}>
                    <Money paise={r.runningBalance} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
